import React, { useEffect, useMemo } from 'react';
import {
  ArrowDownTrayIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  ArrowPathIcon,
  BoltIcon,
  ClockIcon,
  CurrencyDollarIcon,
  CursorArrowRaysIcon,
  ExclamationTriangleIcon,
  IdentificationIcon,
  ShoppingBagIcon,
  UserPlusIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import {
  Card,
  CardHeader,
  Badge,
  Button,
  Spinner,
  Table,
  Pagination,
  SearchInput,
  EmptyState,
  Dropdown,
} from '../components/ui';
import { InsightsTrendChart } from '../components/charts/InsightsTrendChart';
import { ConversionFunnel } from '../components/charts/ConversionFunnel';
import { BreakdownBars } from '../components/charts/BreakdownBars';
import { DeviceSplit } from '../components/charts/DeviceSplit';
import { formatDuration } from '../components/charts/chartTheme';
import { LeadDetailModal } from '../components/modals/LeadDetailModal';
import { useInsightsStore, type RangePreset } from '../stores/insightsStore';
import { useAuthStore } from '../stores';
import { formatCurrency, formatRelativeTime, formatPhone, cn } from '../utils/helpers';
import type { InsightsLead, InsightsLeadFilterStatus } from '../types';

// ============================================
// Stat tile
// ============================================

interface StatTileProps {
  title: string;
  value: string;
  change?: number;
  changeSuffix?: string;
  hint?: string;
  icon: React.ReactNode;
  iconBg: string;
  /** For metrics where a fall is the good outcome (abandonment). */
  invertChange?: boolean;
}

const StatTile: React.FC<StatTileProps> = ({
  title,
  value,
  change,
  changeSuffix = '%',
  hint,
  icon,
  iconBg,
  invertChange = false,
}) => {
  const isUp = change !== undefined && change >= 0;
  const isGood = invertChange ? !isUp : isUp;

  return (
    <Card className="relative overflow-hidden">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-medium text-secondary-500 dark:text-secondary-400 truncate">
            {title}
          </p>
          <p className="mt-2 text-2xl font-bold tabular-nums text-secondary-900 dark:text-secondary-50">
            {value}
          </p>
          {change !== undefined ? (
            <div className="mt-2 flex items-center">
              {isUp ? (
                <ArrowTrendingUpIcon
                  className={cn('h-4 w-4', isGood ? 'text-success-500' : 'text-danger-500')}
                />
              ) : (
                <ArrowTrendingDownIcon
                  className={cn('h-4 w-4', isGood ? 'text-success-500' : 'text-danger-500')}
                />
              )}
              <span
                className={cn(
                  'ml-1 text-sm font-medium tabular-nums',
                  isGood
                    ? 'text-success-600 dark:text-success-400'
                    : 'text-danger-600 dark:text-danger-400'
                )}
              >
                {Math.abs(change)}
                {changeSuffix}
              </span>
              <span className="ml-1 text-sm text-secondary-500 dark:text-secondary-400">
                vs prev period
              </span>
            </div>
          ) : (
            hint && (
              <p className="mt-2 text-sm text-secondary-500 dark:text-secondary-400">{hint}</p>
            )
          )}
        </div>
        <div className={cn('p-3 rounded-xl shrink-0', iconBg)}>{icon}</div>
      </div>
    </Card>
  );
};

// ============================================
// Filters
// ============================================

const RANGE_PRESETS: Array<{ value: Exclude<RangePreset, 'custom'>; label: string }> = [
  { value: '7d', label: '7 days' },
  { value: '30d', label: '30 days' },
  { value: '90d', label: '90 days' },
];

const STATUS_TABS: Array<{ value: InsightsLeadFilterStatus; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'recoverable', label: 'Recoverable' },
  { value: 'abandoned', label: 'Abandoned' },
  { value: 'active', label: 'In progress' },
  { value: 'converted', label: 'Converted' },
];

const STATUS_VARIANT = {
  converted: 'success',
  abandoned: 'danger',
  active: 'warning',
} as const;

const STATUS_LABEL = {
  converted: 'Converted',
  abandoned: 'Abandoned',
  active: 'In progress',
} as const;

// ============================================
// Page
// ============================================

export const Insights: React.FC = () => {
  const { permissions, user } = useAuthStore();
  const {
    preset,
    startDate,
    endDate,
    overview,
    isLoadingOverview,
    leads,
    totalLeads,
    page,
    pageSize,
    statusFilter,
    search,
    isLoadingLeads,
    selectedLead,
    timeline,
    isLoadingTimeline,
    isExporting,
    error,
    fetchOverview,
    fetchLeads,
    setPreset,
    setCustomRange,
    setStatusFilter,
    setSearch,
    selectLead,
    exportCsv,
    refresh,
  } = useInsightsStore();

  const canExport = user?.isSuperAdmin === true || permissions.includes('insights:export');

  useEffect(() => {
    void fetchOverview();
    void fetchLeads(1);
    // Deliberately runs once — range/filter changes re-fetch through their own setters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounced search comes from SearchInput; refetch whenever the committed term changes.
  useEffect(() => {
    void fetchLeads(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const summary = overview?.summary;

  const fieldDropOffRows = useMemo(
    () =>
      (overview?.fieldDropOff.fields || []).map((field) => ({
        key: field.field,
        label: field.label,
        value: field.count,
        meta: `${field.share}%`,
      })),
    [overview]
  );

  const sourceRows = useMemo(
    () =>
      (overview?.sources || []).map((source) => ({
        key: source.source,
        label: source.source,
        value: source.sessions,
        meta: `${source.conversionRate}% conv`,
      })),
    [overview]
  );

  const totalPages = Math.max(1, Math.ceil(totalLeads / pageSize));

  const leadColumns = [
    {
      key: 'name',
      header: 'Visitor',
      render: (lead: InsightsLead) => (
        <div className="min-w-0">
          <p className="font-medium text-secondary-900 dark:text-secondary-50 truncate">
            {lead.fullName || 'Anonymous visitor'}
          </p>
          <p className="text-xs text-secondary-500 dark:text-secondary-400 truncate">
            {lead.city && lead.state ? `${lead.city}, ${lead.state}` : lead.deviceType || '—'}
          </p>
        </div>
      ),
    },
    {
      key: 'contact',
      header: 'Contact',
      render: (lead: InsightsLead) =>
        lead.isReachable ? (
          <div className="min-w-0">
            {lead.email && (
              <p className="text-sm text-secondary-900 dark:text-secondary-50 truncate">{lead.email}</p>
            )}
            {lead.phone && (
              <p className="text-xs text-secondary-500 dark:text-secondary-400">
                {formatPhone(lead.phone)}
              </p>
            )}
          </div>
        ) : (
          <span className="text-sm text-secondary-400 dark:text-secondary-500">No contact captured</span>
        ),
    },
    {
      key: 'furthestStep',
      header: 'Reached',
      sortable: false,
      render: (lead: InsightsLead) => (
        <span className="text-sm text-secondary-700 dark:text-secondary-300">
          {lead.furthestStepLabel}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (lead: InsightsLead) => (
        <Badge variant={STATUS_VARIANT[lead.status]} dot>
          {STATUS_LABEL[lead.status]}
        </Badge>
      ),
    },
    {
      key: 'activeSeconds',
      header: 'Time',
      render: (lead: InsightsLead) => (
        <span className="text-sm tabular-nums text-secondary-700 dark:text-secondary-300">
          {formatDuration(lead.activeSeconds)}
        </span>
      ),
    },
    {
      key: 'cartValue',
      header: 'Cart',
      render: (lead: InsightsLead) => (
        <span className="text-sm tabular-nums text-secondary-900 dark:text-secondary-50">
          {lead.cartValue !== null ? formatCurrency(lead.cartValue) : '—'}
        </span>
      ),
    },
    {
      key: 'lastActivityAt',
      header: 'Last seen',
      render: (lead: InsightsLead) => (
        <span className="text-sm text-secondary-500 dark:text-secondary-400">
          {formatRelativeTime(lead.lastActivityAt)}
        </span>
      ),
    },
  ];

  if (isLoadingOverview && !overview) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header + controls */}
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="max-w-xl">
          <h1 className="text-2xl font-display font-semibold text-secondary-900 dark:text-secondary-50">
            Insights
          </h1>
          <p className="mt-1 text-sm text-secondary-500 dark:text-secondary-400">
            How visitors move through checkout — where they convert, where they drop, and who to follow up with.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* Range presets */}
          <div className="inline-flex rounded-lg border border-secondary-300 dark:border-secondary-600 bg-white dark:bg-secondary-800 p-0.5">
            {RANGE_PRESETS.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => setPreset(option.value)}
                className={cn(
                  'px-3 py-1.5 text-xs font-medium rounded-md transition-colors duration-200',
                  preset === option.value
                    ? 'bg-primary-500 text-white shadow-sm'
                    : 'text-secondary-600 dark:text-secondary-300 hover:bg-secondary-100 dark:hover:bg-secondary-700'
                )}
              >
                {option.label}
              </button>
            ))}
          </div>

          {/* Custom range */}
          <div className="inline-flex items-center gap-1.5 rounded-lg border border-secondary-300 dark:border-secondary-600 bg-white dark:bg-secondary-800 px-2 py-1">
            <input
              type="date"
              value={startDate}
              max={endDate}
              onChange={(event) => setCustomRange(event.target.value, endDate)}
              className="bg-transparent text-xs text-secondary-700 dark:text-secondary-200 focus:outline-none"
              aria-label="Start date"
            />
            <span className="text-secondary-400">–</span>
            <input
              type="date"
              value={endDate}
              min={startDate}
              onChange={(event) => setCustomRange(startDate, event.target.value)}
              className="bg-transparent text-xs text-secondary-700 dark:text-secondary-200 focus:outline-none"
              aria-label="End date"
            />
          </div>

          <Button
            variant="secondary"
            size="md"
            onClick={() => void refresh()}
            leftIcon={<ArrowPathIcon className={cn('h-4 w-4', isLoadingOverview && 'animate-spin')} />}
          >
            Refresh
          </Button>

          {canExport && (
            <Dropdown
              trigger={
                <Button
                  variant="primary"
                  size="md"
                  isLoading={isExporting}
                  leftIcon={<ArrowDownTrayIcon className="h-4 w-4" />}
                  rightIcon={<ChevronDownIcon className="h-4 w-4" />}
                >
                  Export
                </Button>
              }
              items={[
                {
                  label: 'Leads (CSV)',
                  icon: <IdentificationIcon className="h-4 w-4" />,
                  onClick: () => void exportCsv('leads'),
                },
                {
                  label: 'Metrics report (CSV)',
                  icon: <CursorArrowRaysIcon className="h-4 w-4" />,
                  onClick: () => void exportCsv('metrics'),
                },
              ]}
            />
          )}
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-danger-200 dark:border-danger-800 bg-danger-50 dark:bg-danger-900/20 px-4 py-3 text-sm text-danger-700 dark:text-danger-400">
          {error}
        </div>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <StatTile
          title="Checkout Sessions"
          value={(summary?.sessions ?? 0).toLocaleString()}
          change={summary?.changes.sessions}
          icon={<CursorArrowRaysIcon className="h-6 w-6 text-primary-600 dark:text-primary-400" />}
          iconBg="bg-primary-50 dark:bg-primary-900/30"
        />
        <StatTile
          title="Leads Captured"
          value={(summary?.identifiedLeads ?? 0).toLocaleString()}
          change={summary?.changes.identifiedLeads}
          icon={<UserPlusIcon className="h-6 w-6 text-accent-600 dark:text-accent-400" />}
          iconBg="bg-accent-50 dark:bg-accent-900/30"
        />
        <StatTile
          title="Completed Orders"
          value={(summary?.conversions ?? 0).toLocaleString()}
          change={summary?.changes.conversions}
          icon={<ShoppingBagIcon className="h-6 w-6 text-success-600 dark:text-success-400" />}
          iconBg="bg-success-50 dark:bg-success-900/30"
        />
        <StatTile
          title="Revenue"
          value={formatCurrency(summary?.revenue ?? 0)}
          change={summary?.changes.revenue}
          icon={<CurrencyDollarIcon className="h-6 w-6 text-success-600 dark:text-success-400" />}
          iconBg="bg-success-50 dark:bg-success-900/30"
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <StatTile
          title="Conversion Rate"
          value={`${summary?.conversionRate ?? 0}%`}
          change={summary?.changes.conversionRate}
          changeSuffix="pts"
          icon={<BoltIcon className="h-6 w-6 text-primary-600 dark:text-primary-400" />}
          iconBg="bg-primary-50 dark:bg-primary-900/30"
        />
        <StatTile
          title="Abandonment Rate"
          value={`${summary?.abandonmentRate ?? 0}%`}
          hint={`${(summary?.abandoned ?? 0).toLocaleString()} checkouts left unfinished`}
          icon={<ExclamationTriangleIcon className="h-6 w-6 text-danger-600 dark:text-danger-400" />}
          iconBg="bg-danger-50 dark:bg-danger-900/30"
        />
        <StatTile
          title="Recoverable Leads"
          value={(summary?.recoverableLeads ?? 0).toLocaleString()}
          hint="Unfinished checkouts with a contact method"
          icon={<IdentificationIcon className="h-6 w-6 text-warning-600 dark:text-warning-400" />}
          iconBg="bg-warning-50 dark:bg-warning-900/30"
        />
        <StatTile
          title="Avg Time in Checkout"
          value={formatDuration(summary?.avgTimeOnPageSeconds ?? 0)}
          hint={`Median ${formatDuration(summary?.medianTimeOnPageSeconds ?? 0)} · ${formatDuration(
            summary?.avgTimeToConvertSeconds ?? 0
          )} to convert`}
          icon={<ClockIcon className="h-6 w-6 text-secondary-600 dark:text-secondary-300" />}
          iconBg="bg-secondary-100 dark:bg-secondary-700"
        />
      </div>

      {/* Trend + funnel */}
      <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">
        <Card className="xl:col-span-3 flex flex-col">
          <CardHeader
            title="Traffic & Conversion"
            subtitle="Daily checkout sessions, leads captured and completed orders"
          />
          {/* Fills the row height so the card never leaves dead space beside the funnel */}
          <InsightsTrendChart points={overview?.timeseries.points || []} className="flex-1" />
        </Card>

        <Card className="xl:col-span-2">
          <CardHeader
            title="Checkout Funnel"
            subtitle="How far each visitor gets, and where they fall out"
          />
          <ConversionFunnel
            steps={overview?.funnel.steps || []}
            totalSessions={overview?.funnel.totalSessions || 0}
          />
        </Card>
      </div>

      {/* Fall-off, sources, devices */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card>
          <CardHeader
            title="Where They Stop"
            subtitle="Last field completed before abandoning checkout"
          />
          <BreakdownBars
            rows={fieldDropOffRows}
            tone="danger"
            emptyMessage="No abandoned checkouts with captured fields yet."
          />
        </Card>

        <Card>
          <CardHeader title="Traffic Sources" subtitle="Sessions by campaign source or referrer" />
          <BreakdownBars rows={sourceRows} emptyMessage="No traffic attributed yet." />
        </Card>

        <Card>
          <CardHeader title="Devices" subtitle="Session mix and conversion by form factor" />
          <DeviceSplit devices={overview?.devices || []} />
        </Card>
      </div>

      {/* Lead table */}
      <Card padding="none">
        <div className="p-6 pb-4">
          <CardHeader
            title="Checkout Leads"
            subtitle="Everyone who started checkout — including those who never finished"
            action={
              <span className="text-sm text-secondary-500 dark:text-secondary-400 tabular-nums">
                {totalLeads.toLocaleString()} total
              </span>
            }
          />

          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-wrap gap-1.5">
              {STATUS_TABS.map((tab) => (
                <button
                  key={tab.value}
                  type="button"
                  onClick={() => setStatusFilter(tab.value)}
                  className={cn(
                    'px-3 py-1.5 text-xs font-medium rounded-lg transition-colors duration-200',
                    statusFilter === tab.value
                      ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400'
                      : 'text-secondary-600 dark:text-secondary-400 hover:bg-secondary-100 dark:hover:bg-secondary-700/50'
                  )}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            <SearchInput
              value={search}
              onChange={setSearch}
              placeholder="Search name, email, phone or city…"
              className="lg:w-80"
            />
          </div>
        </div>

        {!isLoadingLeads && leads.length === 0 ? (
          <EmptyState
            icon={<IdentificationIcon className="h-8 w-8" />}
            title="No leads in this view"
            description="Once visitors start filling in checkout, their details appear here — even if they never place an order."
          />
        ) : (
          <>
            <Table
              columns={leadColumns}
              data={leads}
              keyExtractor={(lead) => lead.id}
              isLoading={isLoadingLeads}
              onRowClick={(lead) => void selectLead(lead)}
            />
            {totalPages > 1 && (
              <div className="p-4 border-t border-secondary-100 dark:border-secondary-700">
                <Pagination
                  currentPage={page}
                  totalPages={totalPages}
                  onPageChange={(nextPage) => void fetchLeads(nextPage)}
                />
              </div>
            )}
          </>
        )}
      </Card>

      <LeadDetailModal
        lead={selectedLead}
        timeline={timeline}
        isLoadingTimeline={isLoadingTimeline}
        onClose={() => void selectLead(null)}
      />
    </div>
  );
};

export default Insights;
